import {
  ComponentType,
  LegacyRef,
  PropsWithChildren,
  ReactNode,
  forwardRef,
} from "react";

import AppLink from "@components/link/link";
import { toObjectProps } from "@components/link/link-helpers";
import Tooltip from "@components/tooltip/tooltip";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import { titleCase } from "@helpers/string";

import {
  ButtonSize,
  buttonTheme,
  getLeftNegativeClassName,
} from "./button-constants";

type ButtonTheme = keyof typeof buttonTheme;

export interface ButtonProps {
  type?: "button" | "submit" | "reset";
  text?: string;
  testId?: string;
  theme?: ButtonTheme | Omit<string, ButtonTheme>;
  disabled?: boolean;
  className?: string;
  leftNegativeMargin?: boolean;
  size?: ButtonSize;
  rounded?: boolean;
  fullWidth?: boolean;
  square?: boolean;
  icon?: ComponentType<{ className?: string }>;
  url?: string | null;
  to?: string | toObjectProps;
  tooltip?: string | null | false | undefined;
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  "aria-label"?: string;
  target?: string;
  rel?: string;
  children?: ReactNode;
  showDownChevronIcon?: boolean;
}

export type ButtonInSelectProps = Omit<
  ButtonProps,
  | "as"
  | "children"
  | "className"
  | "icon"
  | "showDownChevronIcon"
  | "text"
  | "to"
  | "url"
>;

const Button = forwardRef<
  HTMLAnchorElement | HTMLButtonElement,
  PropsWithChildren<ButtonProps>
>(
  (
    {
      type = "button",
      text,
      testId = "",
      theme = "default",
      disabled = false,
      className = "",
      size = ButtonSize.large,
      icon,
      square,
      leftNegativeMargin = false,
      rounded = false,
      fullWidth = false,
      children,
      onClick,
      url = null,
      to = null,
      tooltip = "",
      "aria-label": ariaLabel,
      target,
      rel,
      showDownChevronIcon = false,
      ...props
    },
    ref,
  ) => {
    if (!!icon && (!!children || !!text)) {
      if (import.meta.env.DEV || !!window.Cypress) {
        throw new Error(
          "Button cannot have both an icon and (text or children) at the same time.",
        );
      }
    }
    if (fullWidth && square) {
      if (import.meta.env.DEV || !!window.Cypress) {
        throw new Error("fullWidth and square cannot be used together");
      }
    }

    let themeClassName = "";
    const defaultThemeClassName = classNames(
      "border-gray-200",
      "bg-white text-gray-700 focus:ring-0 focus:outline-none",
      !disabled && "hover:bg-gray-50",
    );
    if (theme === buttonTheme.primary) {
      themeClassName = classNames(
        "border-transparent",
        "text-white bg-blue-500 focus:ring-blue-500",
        !disabled && "hover:bg-blue-600",
      );
    } else if (theme === buttonTheme.redDanger) {
      themeClassName = classNames(
        "border-red-700 text-red-700 bg-white focus:ring-red-500",
        !disabled && "hover:bg-red-700 hover:text-white",
        disabled && "opacity-70",
      );
    } else if (theme === buttonTheme.lightBlue) {
      themeClassName = classNames(
        "border-transparent",
        "bg-blue-50 text-blue-600 font-normal focus:ring-blue-50",
        !disabled && "hover:bg-blue-100",
      );
    } else if (theme === buttonTheme.lightGray) {
      themeClassName = classNames(
        "border-transparent",
        "bg-gray-100 text-gray-700 font-normal focus:ring-gray-100",
        !disabled && "hover:bg-gray-200",
      );
    } else if (theme === buttonTheme.green) {
      themeClassName = classNames(
        "border border-green-700",
        "text-green-700 font-normal focus:ring-green-50 bg-green-50",
        !disabled && "hover:bg-green-100",
      );
    } else if (
      theme === buttonTheme.text ||
      theme === buttonTheme.textRed ||
      theme === buttonTheme.blueText
    ) {
      themeClassName = classNames(
        "border-transparent text-gray-700 font-normal",
        !disabled && "hover:bg-black/5",
      );
      if (theme === buttonTheme.textRed) {
        themeClassName += " text-red-600";
      }
      if (theme === buttonTheme.blueText) {
        themeClassName += " text-blue-500 ";
        if (!disabled) {
          themeClassName += " hover:bg-blue-50 ";
        }
      }
    } else if (theme === buttonTheme.iconGray) {
      themeClassName = classNames(
        "border-transparent text-gray-400",
        !disabled && "hover:bg-black/5 hover:text-gray-700",
      );
    } else {
      themeClassName = defaultThemeClassName;
    }

    const buttonClassName = classNames(
      "inline-flex justify-center items-center font-medium rounded-[6px] border focus:outline-none shrink-0 flex-nowrap",
      themeClassName,
      disabled ? "opacity-50 cursor-default" : "",
      size === ButtonSize.small ? "text-xs" : "text-sm",
      size === ButtonSize.small && "tracking-tight",
      size === ButtonSize.small
        ? "h-[20px]"
        : size === ButtonSize.large
          ? "h-[32px]"
          : "h-[24px]",
      !icon && !square
        ? null
        : size === ButtonSize.small
          ? "w-[20px]"
          : size === ButtonSize.large
            ? "w-[32px]"
            : "w-[24px]",
      icon || square
        ? null // handled by width
        : size === ButtonSize.small
          ? "px-1"
          : size === ButtonSize.large
            ? "px-3"
            : "px-2",
      !leftNegativeMargin
        ? null
        : icon || square
          ? null
          : getLeftNegativeClassName(size),
      size === ButtonSize.small
        ? "gap-[3px]"
        : size === ButtonSize.large
          ? "gap-[5px]"
          : "gap-[4px]",
      rounded && "rounded-full",
      fullWidth && "w-full justify-start text-left",
      className,
    );

    const handleClick = (
      e: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
    ) => {
      onClick?.(e as React.MouseEvent<HTMLButtonElement>);
    };

    const downChevronIcon = showDownChevronIcon ? (
      <TopicflowIcon
        type="chevronDown"
        className={classNames(
          "h-4 w-4 text-gray-400 shrink-0 pointer-events-none",
        )}
      />
    ) : null;

    const Icon = icon && typeof icon !== "boolean" ? icon : null;
    const titleCaseText = titleCase(text || "");
    const buttonContent = Icon ? (
      <Icon className={classNames("shrink-0", "h-4 w-4")} />
    ) : (
      <>
        {children || titleCaseText}
        {downChevronIcon}
      </>
    );
    const button = to ? (
      <AppLink
        disabled={disabled}
        to={to}
        data-testid={testId}
        className={buttonClassName}
        onClick={handleClick}
        aria-label={ariaLabel}
        target={target}
        rel={rel}
      >
        {buttonContent}
      </AppLink>
    ) : url ? (
      <a
        href={url}
        data-testid={testId}
        className={buttonClassName}
        ref={ref as unknown as LegacyRef<HTMLAnchorElement>}
        onClick={handleClick}
        aria-label={ariaLabel}
        target={target}
        rel={rel}
      >
        {buttonContent}
      </a>
    ) : (
      <button
        ref={ref as unknown as LegacyRef<HTMLButtonElement>}
        type={type}
        disabled={disabled}
        data-testid={testId}
        className={buttonClassName}
        onClick={handleClick}
        aria-label={ariaLabel}
        rel={rel}
        {...props}
      >
        {buttonContent}
      </button>
    );
    if (tooltip) {
      return (
        <Tooltip text={tooltip} delay={1000}>
          <span className="flex items-center min-w-0">{button}</span>
        </Tooltip>
      );
    }
    return button;
  },
);

export default Button;
